import type { ApiPartnerModel as ApiPartner } from "@daytrip/api";
import { ApiPartnerRestrictedZoneModel as ApiPartnerRestrictedZone } from "@daytrip/api";
import autobind from "autobind-decorator";
import { plainToClass } from "class-transformer";
import { action, observable } from "mobx";

import { toastError, toastSuccess } from "../../../components/ui/NotificationToast/notificationToasts";
import { PageStore } from "../../../stores/PageStore";

import type { RestrictedZonesPageRouter } from "./RestrictedZonesPageRouter";
import type { GetApiPartnerRestrictedZonesSortBy, UpsertRestrictedZoneArgs } from "./types/RestrictedZones.types";

@autobind
export class RestrictedZonesPageStore extends PageStore<RestrictedZonesPageRouter, {}> {
    @observable
    public apiPartners?: ApiPartner[];

    @observable
    public apiPartnersCount?: number;

    @observable
    public filteredApiPartners?: ApiPartner[];

    @observable
    public restrictedZones?: ApiPartnerRestrictedZone[];

    @observable
    public restrictedZonesCount?: number;

    @observable
    public skip = 0;

    @observable
    public limit = 10;

    @observable
    public selectedZone?: UpsertRestrictedZoneArgs;

    @observable
    public isModalOpen: boolean = false;

    @action
    public openModal(restrictedZoneId?: string) {
        const selectedZone = this.restrictedZones?.find((zone) => zone._id === restrictedZoneId);
        this.selectedZone = selectedZone
            ? {
                  ...selectedZone,
                  apiPartnerIds: selectedZone.apiPartners.map((p) => p._id),
              }
            : undefined;

        this.isModalOpen = true;
    }

    @action
    public closeModal() {
        this.isModalOpen = false;
        this.selectedZone = undefined;
    }

    @action
    public async onFetchData() {
        await this.fetchContent();
    }

    @action
    public async fetchContent(): Promise<void> {
        await Promise.all([this.fetchRestrictedZones(), this.fetchApiPartners()]);
    }

    @action
    public async fetchRestrictedZones(): Promise<void> {
        const { apiPartnerId, zoneName, skip, take } = this.pageRouter.rawQuery;

        const options = {
            skip: skip ? parseInt(skip as string) : 0,
            limit: take ? parseInt(take as string) : 10,
            sortBy: this.pageRouter.sortBy as GetApiPartnerRestrictedZonesSortBy,
            sortDirection: this.pageRouter.sortDirection,
            filterBy: {
                apiPartnerId: apiPartnerId,
                name: zoneName,
            },
        };

        const { data, total } = await this.rpcClient.apiPartnerRestrictedZones.getApiPartnerRestrictedZones(options);

        this.restrictedZones = plainToClass(ApiPartnerRestrictedZone, data);
        this.restrictedZonesCount = total;
    }

    @action
    public async fetchApiPartners(): Promise<void> {
        const { data, total } = await this.rpcClient.apiPartner.getApiPartners({
            limit: 0,
        });

        this.apiPartners = data;
        this.apiPartnersCount = total;
    }

    @action
    public async searchApiPartners(searchTerm: string): Promise<ApiPartner[]> {
        this.filteredApiPartners = [];

        if (!searchTerm || searchTerm.length < 2) {
            return this.filteredApiPartners;
        }

        const data = await this.rpcClient.apiPartner.getApiPartnersByName(searchTerm);

        this.filteredApiPartners = data;
        return this.filteredApiPartners;
    }

    @action
    public async upsertRestrictedZone(data: UpsertRestrictedZoneArgs) {
        try {
            if (data._id) {
                await this.rpcClient.apiPartnerRestrictedZones.updateApiPartnerRestrictedZone(data._id, data);
                toastSuccess(`Restricted zone ${data.name} updated successfully.`);
            } else {
                await this.rpcClient.apiPartnerRestrictedZones.createApiPartnerRestrictedZone(data);
                toastSuccess(`Restricted zone ${data.name} created successfully.`);
            }
        } catch (error) {
            toastError(`Failed to save restricted zone ${data.name}.`);
        }

        await this.onFetchData();
        this.closeModal();
    }

    @action
    public async deleteRestrictedZone(restrictedZoneId: string) {
        try {
            await this.rpcClient.apiPartnerRestrictedZones.deleteApiPartnerRestrictedZone(restrictedZoneId);
            await this.onFetchData();
            toastSuccess(`Restricted zone deleted successfully.`);
        } catch (error) {
            toastError(`Failed to delete restricted zone.`);
        }
    }

    public isDataFetched(): this is RestrictedZonesPageStore & RestrictedZonesPageStoreDataFetched {
        return Boolean(this.restrictedZones);
    }
}

export interface RestrictedZonesPageStoreDataFetched {
    restrictedZones: ApiPartnerRestrictedZone[];
    restrictedZonesCount: number;
}
